import React, { useState, useEffect } from 'react';
import { Folder, Plus, Edit2, Trash2, Save, X, Tag, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const CategoriesManagement = () => {
  const { user, restaurants: userRestaurants } = useAuth();
  const [categories, setCategories] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Kullanıcı rolünü belirle
  const getUserRole = () => {
    if (!user) return 'User';
    return typeof user.role === 'number' ?
      (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') :
      user.role;
  };

  const isRestaurantUser = getUserRole() !== 'SuperAdmin';

  // Kullanıcının varsayılan restoran ID'sini al
  const getDefaultRestaurantId = () => {
    if (isRestaurantUser && user?.restaurantId) {
      return user.restaurantId;
    }
    if (userRestaurants && userRestaurants.length === 1) {
      return userRestaurants[0].id || userRestaurants[0].restaurantId;
    }
    return '';
  };

  const [formData, setFormData] = useState({
    restaurantId: getDefaultRestaurantId(),
    name: '',
    description: '',
    displayOrder: 0,
    icon: '',
    isActive: true,
    isRootCategory: true,
    parentCategoryId: '',
    nameEn: '',
    descriptionEn: ''
  });

  // Load categories and restaurants from API
  useEffect(() => {
    if (userRestaurants && userRestaurants.length > 0) {
      setRestaurants(userRestaurants);

      // Restaurant kullanıcısı ise kendi restoranını seç, değilse tek restoran varsa onu seç
      const defaultId = getDefaultRestaurantId();
      if (defaultId) {
        setFormData(prev => ({
          ...prev,
          restaurantId: defaultId
        }));
      }
    } else {
      fetchRestaurants();
    }
    fetchCategories();
  }, [userRestaurants]);

  const fetchCategories = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      let url = `${API_BASE_URL}/api/Categories`;
      
      // Convert numeric role to string for comparison
      const userRole = typeof user.role === 'number' ? 
        (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
        user.role;
      
      // If user is not SuperAdmin, filter by their restaurant
      if (userRole !== 'SuperAdmin') {
        url = `${API_BASE_URL}/api/Categories/user/${user.id}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Categories fetched:', data);
        setCategories(data);
      } else {
        setError('Kategoriler yüklenirken hata oluştu');
      }
    } catch (err) {
      setError('API bağlantı hatası');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const fetchRestaurants = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('authToken');
      let url = `${API_BASE_URL}/api/Restaurants`;
      
      // Convert numeric role to string for comparison
      const userRole = typeof user.role === 'number' ? 
        (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
        user.role;
      
      // If user is not SuperAdmin, filter by their restaurant
      if (userRole !== 'SuperAdmin') {
        url = `${API_BASE_URL}/api/Restaurants/user/${user.id}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Restaurants fetched:', data);
        setRestaurants(data);
        
        // Eğer sadece 1 restaurant varsa, onu otomatik seç
        if (data.length === 1) {
          setFormData(prev => ({
            ...prev,
            restaurantId: data[0].id || data[0].restaurantId
          }));
        }
      } else {
        console.error('Restoranlar yüklenirken hata oluştu');
      }
    } catch (err) {
      console.error('Restaurant API Error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      // "Ana kategori" işaretlenince üst kategori seçimini temizle
      if (name === 'isRootCategory' && checked) {
        next.parentCategoryId = '';
      }
      // Restoran değişince üst kategori seçimi geçersiz olur, temizle
      if (name === 'restaurantId') {
        next.parentCategoryId = '';
      }
      return next;
    });
  };

  const resetForm = () => {
    setFormData({
      restaurantId: getDefaultRestaurantId(),
      name: '',
      description: '',
      displayOrder: 0,
      icon: '',
      isActive: true,
      isRootCategory: true,
      parentCategoryId: ''
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    resetForm();
  };

  const handleEdit = (category) => {
    console.log('Editing category:', category);
    
    // Try different possible ID field names
    const possibleIds = [
      category.categoryId,
      category.id, 
      category.CategoryId,
      category.ID
    ];
    
    // Find the first non-null ID
    const actualId = possibleIds.find(id => id != null);
    console.log('Using category ID:', actualId);
    
    if (!actualId) {
      setError('Category ID bulunamadı');
      return;
    }
    
    setError('');
    setIsCreating(false);
    setEditingCategory(actualId);
    
    const parentId = category.parentCategoryId || category.ParentCategoryId || '';
    setFormData({
      restaurantId: category.restaurantId || '',
      name: category.name || '',
      description: category.description || '',
      displayOrder: category.displayOrder || 0,
      icon: category.icon || '',
      isActive: category.isActive !== undefined ? category.isActive : true,
      isRootCategory: !parentId,
      parentCategoryId: parentId,
      nameEn: category.englishName || '',
      descriptionEn: category.englishDescription || ''
    });
    
    console.log('Form data set for editing:', formData);
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!formData.name.trim()) {
        setError('Kategori adı zorunludur');
        return;
      }

      if (!formData.restaurantId || !String(formData.restaurantId).trim()) {
        setError('Restoran seçimi zorunludur');
        return;
      }

      // Prepare request data
      const requestData = {
        restaurantId: String(formData.restaurantId).trim(),
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        displayOrder: parseInt(formData.displayOrder) || 0,
        icon: formData.icon.trim() || null,
        isActive: formData.isActive,
        parentCategoryId: (!formData.isRootCategory && formData.parentCategoryId) ? formData.parentCategoryId : null,
        englishName: (formData.nameEn || '').trim(),
        englishDescription: (formData.descriptionEn || '').trim()
      };

      console.log('Category Request Data:', JSON.stringify(requestData, null, 2));
      console.log('Is Edit Mode:', !!editingCategory);
      console.log('Editing Category ID:', editingCategory);

      let response;
      let url;
      
      const token = localStorage.getItem('authToken');

      if (editingCategory) {
        // Update existing category
        url = `${API_BASE_URL}/api/Categories/${editingCategory}`;
        console.log('PUT URL:', url);

        response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
      } else {
        // Create new category
        url = `${API_BASE_URL}/api/Categories`;
        console.log('POST URL:', url);

        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
      }

      console.log('Response Status:', response.status);
      console.log('Response OK:', response.ok);

      if (response.ok) {
        console.log('Save successful!');
        await fetchCategories();
        setIsCreating(false);
        setEditingCategory(null);
        resetForm();
      } else if (response.status === 500) {
        // Check if it's the EF Core trigger issue
        const errorText = await response.text();
        console.log('Error Response:', errorText);
        
        if (errorText.includes('database triggers') || errorText.includes('OUTPUT clause')) {
          console.warn('EF Core trigger issue detected, checking if data was actually saved...');
          
          setTimeout(async () => {
            await fetchCategories();
            
            const savedCategory = categories.find(c => c.name === requestData.name && c.restaurantId === requestData.restaurantId);
            if (savedCategory) {
              console.log('Data was saved successfully despite the error!');
              setIsCreating(false);
              setEditingCategory(null);
              resetForm();
              setError('');
            } else {
              setError(`EF Core Hatası: ${errorText}`);
            }
          }, 1000);
        } else {
          setError(`Server Hatası (500): ${errorText}`);
        }
      } else if (response.status === 400) {
        // Bad Request - usually validation error
        const errorText = await response.text();
        console.error('400 Bad Request Error:', errorText);
        
        try {
          const errorJson = JSON.parse(errorText);
          console.error('Parsed error:', errorJson);
          
          // Check for validation errors
          if (errorJson.errors) {
            const validationErrors = Object.entries(errorJson.errors)
              .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
              .join('; ');
            setError(`Validation Hatası: ${validationErrors}`);
          } else if (errorJson.title) {
            setError(`API Hatası: ${errorJson.title}`);
          } else {
            setError(`Bad Request: ${errorText}`);
          }
        } catch {
          setError(`Bad Request (400): ${errorText}`);
        }
      } else {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        setError(`API Hatası (${response.status}): ${errorText || 'Bilinmeyen hata'}`);
      }
    } catch (err) {
      console.error('Network/Request Error:', err);
      setError(`Bağlantı hatası: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingCategory(null);
    resetForm();
    setError('');
  };

  const handleDelete = async (categoryId) => {
    console.log('handleDelete called with ID:', categoryId);

    if (!categoryId) {
      setError('Category ID bulunamadı');
      return;
    }

    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('authToken');
        console.log('Deleting category with ID:', categoryId);

        const response = await fetch(`${API_BASE_URL}/api/Categories/${categoryId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('Delete Response Status:', response.status);

        if (response.ok || response.status === 204) {
          console.log('Delete successful!');
          await fetchCategories();
        } else if (response.status === 409) {
          const data = await response.json().catch(() => null);
          setError(data?.message || 'Bu kategori silinemez çünkü bağlı ürünleri var. Önce ürünleri silmeniz veya başka bir kategoriye taşımanız gerekiyor.');
        } else if (response.status === 500) {
          const errorText = await response.text();
          if (errorText.includes('REFERENCE constraint') || errorText.includes('FK_')) {
            setError('Bu kategori silinemez çünkü bağlı ürünleri var. Önce ürünleri silmeniz veya başka bir kategoriye taşımanız gerekiyor.');
          } else {
            setError(`Server Hatası (500): ${errorText}`);
          }
        } else {
          const errorText = await response.text();
          console.error('Delete error response:', errorText);
          setError(`Silme Hatası (${response.status}): ${errorText || 'Bilinmeyen hata'}`);
        }
      } catch (err) {
        setError(`Bağlantı hatası: ${err.message}`);
        console.error('Delete Error:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleCategoryStatus = async (category) => {
    try {
      // Get the actual ID
      const possibleIds = [category.categoryId, category.id, category.CategoryId, category.ID];
      const actualId = possibleIds.find(id => id != null);
      
      if (!actualId) {
        setError('Category ID bulunamadı');
        return;
      }

      const updatedData = {
        restaurantId: category.restaurantId,
        name: category.name,
        description: category.description || null,
        displayOrder: category.displayOrder || 0,
        icon: category.icon || null,
        isActive: !category.isActive,
        parentCategoryId: category.parentCategoryId || category.ParentCategoryId || null
      };

      console.log('Toggling category status:', actualId, 'New status:', !category.isActive);

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/Categories/${actualId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedData)
      });

      if (response.ok) {
        await fetchCategories();
      } else if (response.status === 500) {
        const errorText = await response.text();
        if (errorText.includes('database triggers') || errorText.includes('OUTPUT clause')) {
          setTimeout(async () => {
            await fetchCategories();
            setError('');
          }, 1000);
        } else {
          setError(`Durum güncellenirken hata oluştu: ${errorText}`);
        }
      } else {
        const errorText = await response.text();
        setError(`Durum güncellenirken hata oluştu: ${errorText}`);
      }
    } catch (err) {
      setError('API bağlantı hatası');
      console.error('Toggle Error:', err);
    }
  };

  const getRestaurantName = (restaurantId) => {
    const restaurant = restaurants.find(r => {
      const possibleIds = [r.restaurantId, r.id, r.RestaurantId, r.ID];
      return possibleIds.includes(restaurantId);
    });
    return restaurant ? restaurant.name : restaurantId;
  };

  const getRestaurantId = (restaurant) => {
    const possibleIds = [restaurant.restaurantId, restaurant.id, restaurant.RestaurantId, restaurant.ID];
    return possibleIds.find(id => id != null);
  };

  const getCategoryId = (category) => {
    const possibleIds = [category.categoryId, category.id, category.CategoryId, category.ID];
    return possibleIds.find(id => id != null);
  };

  // Üst kategori adayları: aynı restorandaki, düzenlenen kategorinin kendisi hariç kategoriler
  const getAvailableParentCategories = () => {
    return categories.filter(category => {
      const catRestId = String(category.restaurantId || category.RestaurantId || '');
      const catId = getCategoryId(category);
      const editingId = editingCategory ? String(editingCategory) : null;
      return catRestId === String(formData.restaurantId) && String(catId) !== editingId;
    });
  };

  const getCategoryNameById = (id) => {
    if (!id) return '-';
    const cat = categories.find(c => String(getCategoryId(c)) === String(id));
    return cat ? cat.name : '-';
  };

  const getParentId = (category) => category.parentCategoryId || category.ParentCategoryId || null;

  // Icon kodunu emoji'ye çevir
  const getIconEmoji = (iconCode) => {
    const iconMap = {
      'fa-pizza-slice': '🍕',
      'fa-hamburger': '🍔',
      'fa-leaf': '🥗',
      'fa-utensils': '🍜',
      'fa-fish': '🍣',
      'fa-drumstick-bite': '🥩',
      'fa-bread-slice': '🥪',
      'fa-taco': '🌮',
      'fa-french-fries': '🍟',
      'fa-cake-candles': '🍰',
      'fa-ice-cream': '🍦',
      'fa-mug-hot': '☕',
      'fa-beer-mug-empty': '🍺',
      'fa-wine-glass': '🍷',
      'fa-bottle-water': '🥤',
      'fa-apple-whole': '🧃',
      'fa-smoking': '💨',
      'fa-cookie-bite': '🍪',
      'fa-circle': '🍩',
      'fa-candy-cane': '🍫',
      'fa-seedling': '🥜',
      'fa-carrot': '🥕',
      'fa-cheese': '🧀',
      'fa-egg': '🥚',
      'fa-square': '🧇',
      'fa-bacon': '🥓',
      'fa-hotdog': '🌭',
      'fa-glass-water': '🥤',
      'fa-martini-glass': '🍸',
      'fa-martini-glass-citrus': '🍸',
      'fa-wine-bottle': '🍾',
      'fa-champagne-glasses': '🥂',
      'fa-whiskey-glass': '🥃',
      'fa-mug-saucer': '☕',
      'fa-bowl-food': '🍲',
      'fa-plate-wheat': '🍝',
      'fa-shrimp': '🍤',
      'fa-lemon': '🍋',
      'fa-pepper-hot': '🌶️'
    };
    if (iconMap[iconCode]) return iconMap[iconCode];
    // Bilinmeyen fa- kodu ise ham metin gösterme, varsayılan ikon
    if (typeof iconCode === 'string' && iconCode.startsWith('fa-')) return '🍽️';
    return iconCode || '🍽️';
  };

  return (
    <div className="max-w-6xl mx-auto px-3 py-4 sm:p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="border-b border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Folder className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Kategori Yönetimi</h1>
                <p className="text-sm sm:text-base text-gray-600">Menü kategorilerini ekleyin ve düzenleyin</p>
              </div>
            </div>
            {!isCreating && !editingCategory && (
              <button
                onClick={handleCreate}
                className="flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors w-full sm:w-auto"
              >
                <Plus className="h-4 w-4" />
                <span>Yeni Kategori</span>
              </button>
            )}
          </div>
        </div>

        {/* Form */}
        {(isCreating || editingCategory) && (
          <div className="border-b border-gray-200 p-4 sm:p-6 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingCategory ? 'Kategoriyi Düzenle' : 'Yeni Kategori Ekle'}
            </h2>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Restoran *
                </label>
                <select
                  name="restaurantId"
                  value={formData.restaurantId}
                  onChange={handleInputChange}
                  disabled={isRestaurantUser}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${isRestaurantUser ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Restoran seçiniz</option>
                  {restaurants.map((restaurant) => {
                    const restaurantId = getRestaurantId(restaurant);
                    return (
                      <option key={restaurantId} value={restaurantId}>
                        {restaurant.name}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kategori Adı *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Kategori adını girin"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <label className="block text-xs font-medium text-gray-500 mt-2 mb-1">
                  Kategori Adı (İngilizce) <span className="text-gray-400 font-normal">(opsiyonel)</span>
                </label>
                <input
                  type="text"
                  name="nameEn"
                  value={formData.nameEn}
                  onChange={handleInputChange}
                  placeholder="Category name (English)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div className="md:col-span-2 flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isRootCategory"
                  name="isRootCategory"
                  checked={formData.isRootCategory}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="isRootCategory" className="text-sm font-medium text-gray-700">
                  Ana kategori (bir üst kategoriye bağlı değil)
                </label>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Üst Kategori {formData.isRootCategory ? <span className="text-gray-400 font-normal">(ana kategori seçili)</span> : <span className="text-red-500">*</span>}
                </label>
                <select
                  name="parentCategoryId"
                  value={formData.parentCategoryId}
                  onChange={handleInputChange}
                  disabled={formData.isRootCategory || !formData.restaurantId}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${(formData.isRootCategory || !formData.restaurantId) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">{!formData.restaurantId ? 'Önce restoran seçiniz' : 'Üst kategori seçiniz'}</option>
                  {getAvailableParentCategories().map((category) => {
                    const categoryId = getCategoryId(category);
                    return (
                      <option key={categoryId} value={categoryId}>
                        {category.name}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Kategori açıklaması"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <label className="block text-xs font-medium text-gray-500 mt-2 mb-1">
                  Açıklama (İngilizce) <span className="text-gray-400 font-normal">(opsiyonel)</span>
                </label>
                <textarea
                  name="descriptionEn"
                  value={formData.descriptionEn}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Description (English)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sıralama
                </label>
                <input
                  type="number"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  İkon <span className="text-gray-400 font-normal">(opsiyonel)</span>
                </label>
                <select
                  name="icon"
                  value={formData.icon}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">İkon seçin</option>
                  <option value="fa-pizza-slice">🍕 Pizza</option>
                  <option value="fa-hamburger">🍔 Burger</option>
                  <option value="fa-leaf">🥗 Salata</option>
                  <option value="fa-utensils">🍜 Noodle</option>
                  <option value="fa-fish">🍣 Sushi</option>
                  <option value="fa-drumstick-bite">🥩 Et</option>
                  <option value="fa-drumstick-bite">🍗 Tavuk</option>
                  <option value="fa-fish">🐟 Balık</option>
                  <option value="fa-bread-slice">🥪 Sandviç</option>
                  <option value="fa-taco">🌮 Taco</option>
                  <option value="fa-french-fries">🍟 Patates</option>
                  <option value="fa-cake-candles">🍰 Tatlı</option>
                  <option value="fa-ice-cream">🍦 Dondurma</option>
                  <option value="fa-mug-hot">☕ İçecek</option>
                  <option value="fa-beer-mug-empty">🍺 Bira</option>
                  <option value="fa-wine-glass">🍷 Şarap</option>
                  <option value="fa-bottle-water">🥤 Gazlı İçecek</option>
                  <option value="fa-apple-whole">🧃 Meyve Suyu</option>
                  <option value="fa-mug-hot">🍵 Çay</option>
                  <option value="fa-bottle-water">🥛 Süt</option>
                  <option value="fa-smoking">💨 Nargile</option>
                  <option value="fa-cookie-bite">🍪 Kurabiye</option>
                  <option value="fa-circle">🍩 Donut</option>
                  <option value="fa-candy-cane">🍫 Çikolata</option>
                  <option value="fa-candy-cane">🍭 Şeker</option>
                  <option value="fa-circle">🍬 Bonbon</option>
                  <option value="fa-bottle-water">🍯 Bal</option>
                  <option value="fa-seedling">🥜 Fındık</option>
                  <option value="fa-seedling">🌰 Kestane</option>
                  <option value="fa-seedling">🥑 Avokado</option>
                  <option value="fa-seedling">🥦 Brokoli</option>
                  <option value="fa-carrot">🥕 Havuç</option>
                  <option value="fa-apple-whole">🍅 Domates</option>
                  <option value="fa-leaf">🥬 Marul</option>
                  <option value="fa-cheese">🧀 Peynir</option>
                  <option value="fa-egg">🥚 Yumurta</option>
                  <option value="fa-bread-slice">🥖 Ekmek</option>
                  <option value="fa-bread-slice">🍞 Çörek</option>
                  <option value="fa-bread-slice">🥐 Kruvasan</option>
                  <option value="fa-circle">🥨 Simit</option>
                  <option value="fa-circle">🥯 Bagel</option>
                  <option value="fa-egg">🍳 Omlet</option>
                  <option value="fa-circle">🥞 Pancake</option>
                  <option value="fa-square">🧇 Waffle</option>
                  <option value="fa-bacon">🥓 Pastırma</option>
                  <option value="fa-hotdog">🌭 Hot Dog</option>
                  <option value="fa-seedling">🍿 Patlamış Mısır</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                    Kategori aktif
                  </label>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-6">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Kaydediliyor...' : 'Kaydet'}</span>
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
                <span>İptal</span>
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && !isCreating && !editingCategory && (
          <div className="mx-4 sm:mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Categories List */}
        <div className="p-4 sm:p-6">
          {loading && categories.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Yükleniyor...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-12">
              <Folder className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz kategori eklenmemiş</h3>
              <p className="text-gray-600 mb-4">İlk kategorinizi ekleyerek başlayın</p>
              <button
                onClick={handleCreate}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                Kategori Ekle
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <div key={category.categoryId || category.id} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                      {category.icon && (
                        <span className="text-xl sm:text-2xl flex-shrink-0">{getIconEmoji(category.icon)}</span>
                      )}
                      <Tag className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <button
                        onClick={() => toggleCategoryStatus(category)}
                        className={`p-1 rounded-full ${category.isActive ? 'bg-green-500' : 'bg-gray-500'} text-white flex-shrink-0`}
                      >
                        {category.isActive ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      </button>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <button
                        onClick={() => handleEdit(category)}
                        className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          const possibleIds = [category.categoryId, category.id, category.CategoryId, category.ID];
                          const actualId = possibleIds.find(id => id != null);
                          if (actualId) {
                            handleDelete(actualId);
                          } else {
                            setError('Category ID bulunamadı');
                          }
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900 text-sm sm:text-base">{category.name}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        getParentId(category)
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {getParentId(category) ? 'Alt Kategori' : 'Ana Kategori'}
                      </span>
                    </div>

                    {getParentId(category) && (
                      <p className="text-xs text-gray-500">Üst Kategori: {getCategoryNameById(getParentId(category))}</p>
                    )}

                    {category.description && (
                      <p className="text-gray-600 text-xs sm:text-sm line-clamp-2">{category.description}</p>
                    )}

                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-gray-500">
                        Sıralama: {category.displayOrder || 0}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        category.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {category.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>

                    <div className="text-xs text-gray-500 space-y-1">
                      <div className="truncate">Restoran: {getRestaurantName(category.restaurantId)}</div>
                      {category.createdDate && (
                        <div>Oluşturulma: {formatDate(category.createdDate)}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoriesManagement;